/**
 * Drag-and-drop payloads (pragmatic-drag-and-drop). Elements register as
 * draggables/drop targets with these typed payloads; `BoardCanvas` monitors
 * drops and turns them into `CardTarget` / `ColumnTarget` moves.
 *
 * DnD is an accelerator, not the only way: every move is also available via
 * buttons (column menu, card dialog) for keyboard and touch users.
 */
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/extract-closest-edge';

import type { CardTarget, ColumnTarget } from '@/entities/board';

type Data = Record<string | symbol, unknown>;

export const cardData = (cardId: string, columnId: string) => ({ type: 'card', cardId, columnId });
export const columnCardsData = (columnId: string) => ({ type: 'column-cards', columnId });
export const columnData = (columnId: string) => ({ type: 'column', columnId });

export function isCard(data: Data): data is Data & ReturnType<typeof cardData> {
  return data.type === 'card' && typeof data.cardId === 'string';
}

export function isColumnCards(data: Data): data is Data & ReturnType<typeof columnCardsData> {
  return data.type === 'column-cards' && typeof data.columnId === 'string';
}

export function isColumn(data: Data): data is Data & ReturnType<typeof columnData> {
  return data.type === 'column' && typeof data.columnId === 'string';
}

/** Innermost drop target -> card move target. */
export function cardTargetFromDrop(target: Data): CardTarget | null {
  if (isCard(target)) {
    const edge = extractClosestEdge(target);
    return {
      columnId: target.columnId,
      anchorCardId: target.cardId,
      edge: edge === 'top' ? 'before' : 'after',
    };
  }
  if (isColumnCards(target)) return { columnId: target.columnId, anchorCardId: null };
  return null;
}

export function columnTargetFromDrop(target: Data): ColumnTarget | null {
  if (!isColumn(target)) return null;
  const edge = extractClosestEdge(target);
  return { anchorColumnId: target.columnId, edge: edge === 'left' ? 'before' : 'after' };
}
